import { useCallback, useRef, useEffect } from 'react';

import { calculateClampedPan } from '../canvas/viewport-logic';

export function useMapCamera(
    dimensions: { width: number, height: number },
    mapWidth: number = 1024,
    mapHeight: number = 1024
) {
    const zoomRef = useRef(1);
    const panRef = useRef({ x: 0, y: 0 });

    // Compute render dimensions based on aspect ratio
    const getRenderDims = useCallback(() => {
        const { width, height } = dimensions;
        if (width <= 0 || height <= 0) return { rWidth: 0, rHeight: 0 };
        if (mapWidth === 1024 && mapHeight === 1024) {
            const mapSize = Math.min(width, height);
            return { rWidth: mapSize, rHeight: mapSize };
        }
        const ratio = mapWidth / mapHeight;
        if (width / height > ratio) {
            return { rWidth: height * ratio, rHeight: height };
        } else {
            return { rWidth: width, rHeight: width / ratio };
        }
    }, [dimensions, mapWidth, mapHeight]);

    const resetView = useCallback(() => {
        zoomRef.current = 1;
        const { rWidth, rHeight } = getRenderDims();
        panRef.current = calculateClampedPan({ x: 0, y: 0 }, 1, dimensions, rWidth, rHeight);
    }, [dimensions, getRenderDims]);

    useEffect(() => {
        if (dimensions.width > 0 && dimensions.height > 0) {
            const { rWidth, rHeight } = getRenderDims();
            panRef.current = calculateClampedPan(panRef.current, zoomRef.current, dimensions, rWidth, rHeight);
        }
    }, [dimensions, getRenderDims]);

    const updatePan = useCallback((newPan: { x: number, y: number }) => {
        const { rWidth, rHeight } = getRenderDims();
        panRef.current = calculateClampedPan(newPan, zoomRef.current, dimensions, rWidth, rHeight);
    }, [dimensions, getRenderDims]);

    const updateZoom = useCallback((newZoom: number) => {
        zoomRef.current = Math.min(Math.max(newZoom, 1.0), 5);
        const { rWidth, rHeight } = getRenderDims();
        panRef.current = calculateClampedPan(panRef.current, zoomRef.current, dimensions, rWidth, rHeight);
    }, [dimensions, getRenderDims]);

    const zoomIn = useCallback(() => {
        updateZoom(zoomRef.current * 1.2);
    }, [updateZoom]);

    const zoomOut = useCallback(() => {
        updateZoom(zoomRef.current / 1.2);
    }, [updateZoom]);

    return {
        zoomRef,
        panRef,
        resetView,
        zoomIn,
        zoomOut,
        updatePan,
        updateZoom
    };
}
